// PATH: src/components/modules/AgentApp/components/PlannerSessionForm.js

import React, { useState, useEffect } from 'react';
import {
  createPlannerSession,
  updatePlannerSession,
  getPlannerSessionSummary,
  getPlannerSessions,
} from '../services/plannerSessionService';
import PlannerSessionFields from './PlannerSessionFields';
import PlannerSessionSummary from './PlannerSessionSummary';
import PlannerSessionGoalsModal from './PlannerSessionGoalsModal';

const PlannerSessionForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    session_id: '',
    proposed_change_name: '',
    draft_scope: '',
  });
  const [sessionSummary, setSessionSummary] = useState(null);
  const [mode, setMode] = useState('create');
  const [availableSessions, setAvailableSessions] = useState([]);
  const [showGoalsModal, setShowGoalsModal] = useState(false);

  useEffect(() => {
    async function fetchSessions() {
      try {
        const sessions = await getPlannerSessions();
        setAvailableSessions(sessions);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
      }
    }
    fetchSessions();
  }, []);

  // NEW: Deep-link support (Dev IDE -> Agent App)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const psid = params.get("planner_session_id");
      const openGoals = params.get("open_goals");

      if (psid) {
        setFormData((prev) => ({
          ...prev,
          session_id: String(psid),
        }));
        setMode('edit');

        const open =
          openGoals === "1" ||
          String(openGoals || "").toLowerCase() === "true" ||
          String(openGoals || "").toLowerCase() === "yes";

        if (open) {
          setShowGoalsModal(true);
        }
      }
    } catch (e) {
      // keep silent - no regression risk
      console.warn("PlannerSessionForm deep-link parse failed:", e);
    }
  }, []);

  useEffect(() => {
    async function fetchSummary() {
      if (!formData.session_id) {
        setSessionSummary(null);
        return;
      }
      try {
        const summary = await getPlannerSessionSummary(formData.session_id);
        setSessionSummary(summary);
      } catch (err) {
        console.error('Failed to fetch session summary:', err);
        setSessionSummary(null);
      }
    }
    fetchSummary();
  }, [formData.session_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (mode === 'create') {
        const created = await createPlannerSession(formData);
        setFormData({
          session_id: created.session_id,
          proposed_change_name: created.proposed_change_name || '',
          draft_scope: created.draft_scope || '',
        });
        setMode('edit');
      } else {
        await updatePlannerSession(formData.session_id, formData);
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to save planner session:', err);
      alert('Failed to save planner session.');
    }
  };

  const handleNewSession = () => {
    setFormData({ session_id: '', proposed_change_name: '', draft_scope: '' });
    setMode('create');
    setShowGoalsModal(false);
  };

  const handleSessionSelect = (e) => {
    const sessionId = e.target.value;
    setFormData((prev) => ({ ...prev, session_id: sessionId }));
    if (sessionId) setMode('edit');
  };

  const handleManageGoalsClick = () => {
    setShowGoalsModal(true);
  };

  return (
    <div>
      <h2>{mode === 'create' ? '🆕 New Planner Session' : `✏️ Editing Session: ${formData.session_id}`}</h2>

      <div style={{ marginBottom: '10px' }}>
        <select value={formData.session_id} onChange={handleSessionSelect}>
          <option value="">Select Session</option>
          {availableSessions.map((s) => (
            <option key={s.session_id} value={s.session_id}>
              {s.session_id}
            </option>
          ))}
        </select>

        <button onClick={handleNewSession} style={{ marginLeft: '10px' }}>
          + New Session
        </button>

        {formData.session_id && (
          <button onClick={handleManageGoalsClick} style={{ marginLeft: '10px' }}>
            Manage Goals
          </button>
        )}
      </div>

      <PlannerSessionFields
        formData={formData}
        setFormData={setFormData}
        mode={mode}
        handleSubmit={handleSubmit}
      />

      <PlannerSessionSummary sessionSummary={sessionSummary} />

      {showGoalsModal && (
        <PlannerSessionGoalsModal
          sessionId={formData.session_id}
          open={showGoalsModal}
          onClose={() => setShowGoalsModal(false)}
        />
      )}
    </div>
  );
};

export default PlannerSessionForm;